import React from 'react';
import { Link } from 'react-router-dom';
import { trackEvent, BOOKING_SUPERVISION_URL, EMAIL_ADDRESS } from '../lib/tracking';
import { optimizeImage } from '../lib/image';

export default function Supervision() {
  return (
    <div className="bg-[#ECEEF1]">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-3">
              Faglig sparring & udvikling
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#5A232E] tracking-tight leading-[1.12]">
              Supervision, konflikthåndtering & workshops
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#6c7480] leading-relaxed">
              Supervision, konflikthåndtering og foredrag – med fokus på hjernen, relationer og forandring. For psykoterapeuter, lærere, pædagoger og organisationer.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={BOOKING_SUPERVISION_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('cta_booking_click', { location: 'supervision_hero' })}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
              >
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                <span>Book supervision</span>
              </a>
              <a
                href={`mailto:${EMAIL_ADDRESS}?subject=Forespørgsel på supervision eller workshop`}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#ECEEF1] text-[#5A232E] font-medium text-sm border border-[#D9DDE2] hover:bg-[#D9DDE2] transition-colors no-break whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope" aria-hidden="true" />
                <span>Kontakt for tilbud</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 1. Gruppesupervision for fagpersoner */}
      <section className="py-16 sm:py-20 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E]">
                Gruppesupervision
              </span>
              <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
                Gruppesupervision for fagpersoner
              </h2>
              <div className="text-sm sm:text-base text-[#11161d] space-y-4 leading-relaxed">
                <p>
                  Er du terapeut, lærer, pædagog eller arbejder du med mennesker i sårbare positioner? Jeg tilbyder professionel gruppesupervision med fokus på nervesystem, relationel praksis og selvomsorg.
                </p>
                <p>
                  Supervisionen skaber et trygt rum til refleksion, faglig sparring og forebyggelse af udbrændthed. Gennem neuroaffektiv forståelse og et traumeinformeret blik får du nye perspektiver på din praksis.
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-[#6c7480]">
                  <li>Få faglig sparring og perspektiver på klienter og arbejdsmetoder</li>
                  <li>Dykke ned i dine cases og få supervision fra en erfaren supervisor</li>
                  <li>Små grupper med 4-6 deltagere for maksimal fordybelse</li>
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-white border border-[#D9DDE2] text-xs text-[#5A232E] flex flex-wrap gap-4 font-medium items-center justify-between">
                <div className="flex flex-wrap gap-4">
                  <span>Varighed: 3 timer</span>
                  <span>•</span>
                  <span>Pris: 1.250 kr. pr. deltager</span>
                  <span>•</span>
                  <span>Sted: Online eller fysisk på Amager</span>
                </div>
                <Link to="/gruppesupervision/" className="font-semibold underline hover:text-[#431722]">
                  Læs mere om gruppesupervision →
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white">
                <img
                  src={optimizeImage(
                    'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/unsplash-wd1lrb9oeeo.jpg',
                    700
                  )}
                  alt="Gruppesupervision i København"
                  className="w-full h-auto object-cover max-h-[380px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Konflikthåndtering & Mægling */}
      <section className="py-16 sm:py-20 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-[#ECEEF1]">
                <img
                  src={optimizeImage(
                    'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/unsplash-0dso2fc54ug.jpg',
                    700
                  )}
                  alt="Konflikthåndtering og mægling i teams"
                  className="w-full h-auto object-cover max-h-[380px]"
                />
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E]">
                Organisationer & Teams
              </span>
              <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
                Konflikthåndtering og mægling
              </h2>
              <div className="text-sm sm:text-base text-[#11161d] space-y-4 leading-relaxed">
                <p>
                  Konflikter er en naturlig del af enhver relation – på arbejdspladsen, i familier eller blandt kolleger. Men når uenighederne vokser, kan det skabe frustration, dårlig stemning og blokere for samarbejdet.
                </p>
                <p>
                  Som neutral tredjepart hjælper jeg som mægler med at afdække kerneproblemerne, skabe en tryg ramme for dialogen og lede parterne frem mod holdbare win-win-løsninger, hvor alle føler sig hørt.
                </p>
                <p className="text-xs text-[#6c7480]">
                  Et forløb indeholder: Forberedelsesfase med individuelle samtaler, Faciliteringsfase med fælles mæglermøde og Opfølgningsfase med rapport og status.
                </p>
                <div className="pt-2">
                  <Link to="/konflikthaandtering/" className="text-xs font-semibold text-[#5A232E] hover:underline">
                    Gå til fuld underside om konflikthåndtering →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Foredrag & Workshops */}
      <section className="py-16 sm:py-20 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Inspiration & Viden
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Foredrag, oplæg og workshops
            </h2>
            <p className="text-sm sm:text-base text-[#6c7480] mt-3">
              Praksisnære og engagerende arrangementer med konkrete værktøjer for ledere og medarbejdere:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Værdiafklaring', desc: 'Skab klarhed og strategisk retning, der styrker beslutningsprocesser og medarbejdertrivsel.' },
              { title: 'Assertiv kommunikation', desc: 'Tydelig og respektfuld dialog. Sig din mening konstruktivt og håndter svære samtaler.' },
              { title: 'Coachingteknikker for ledere', desc: 'Metoder til at motivere, engagere og fastholde dine medarbejdere.' },
              { title: 'Effektiv kommunikation', desc: 'Kommunikation er nøglen til lederskab. Få budskabet igennem og reducer misforståelser.' },
              { title: 'Konflikthåndtering', desc: 'Lær at identificere, adressere og omdanne konflikter til udvikling og samarbejde.' },
              { title: 'Motivation & potentiale', desc: 'Indsigt i motivationsfaktorer og strategier, der vækker teamets fulde potentiale.' },
            ].map((topic, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-[#D9DDE2] shadow-2xs">
                <div className="w-8 h-8 rounded-lg bg-[#5A232E]/10 text-[#5A232E] flex items-center justify-center text-xs mb-3">
                  ✔
                </div>
                <h3 className="font-display text-lg text-[#5A232E] mb-2">{topic.title}</h3>
                <p className="text-xs text-[#6c7480] leading-relaxed">{topic.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center flex flex-wrap justify-center gap-4">
            <Link
              to="/foredrag-og-workshops/"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm transition-colors"
            >
              <span>Se alle foredrag & workshops →</span>
            </Link>
            <a
              href={`mailto:${EMAIL_ADDRESS}?subject=Forespørgsel på foredrag eller workshop`}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white hover:bg-[#ECEEF1] text-[#5A232E] font-semibold text-sm border border-[#D9DDE2] transition-colors"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span>Indhent uforpligtende tilbud på workshop</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
